import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { useApi } from '../hooks/useApi';
import { categoriesAPI } from '../api/client';
import * as LucideIcons from 'lucide-react';

const CategoryPage = ({ type }) => {
  const { data: categories, loading, error, refetch } = useApi(categoriesAPI.getAll);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner size="lg" text="Cargando categorías..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage error={error} onRetry={refetch} />
        </div>
      </div>
    );
  }

  const category = categories?.[type];

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Categoría no encontrada</h1>
          <Link to="/">
            <Button>Volver al inicio</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-emerald-600 transition-colors">
            Inicio
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium">{category.title}</span>
        </div>

        {/* Back Button */}
        <Button 
          asChild 
          variant="ghost" 
          className="mb-6 hover:bg-emerald-50 hover:text-emerald-700"
        >
          <Link to="/" className="inline-flex items-center space-x-2">
            <ArrowLeft className="w-4 h-4" />
            <span>Volver al inicio</span>
          </Link>
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {category.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {category.description}
          </p>
        </div>

        {/* Subcategories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {category.subcategories.map((subcategory) => {
            const IconComponent = LucideIcons[subcategory.icon] || LucideIcons.FileText;
            
            return (
              <Card 
                key={subcategory.id} 
                className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-emerald-200 cursor-pointer"
              >
                <Link to={`/categoria/${type}/${subcategory.id}`}>
                  <CardHeader className="text-center pb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-2xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                      <IconComponent className="w-7 h-7 text-emerald-600" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors">
                      {subcategory.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {subcategory.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-center pt-0">
                    <div className="inline-flex items-center space-x-2 text-emerald-600 group-hover:text-emerald-700 transition-colors">
                      <span className="font-medium">Ver proyectos</span>
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Link>
              </Card>
            );
          })}
        </div>

        {/* Stats Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Estadísticas de la Categoría
            </h2>
            <p className="text-gray-600">
              Proyectos desarrollados en esta área de investigación
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-1">
                {category.subcategories.length}
              </div>
              <div className="text-sm text-gray-600">Subcategorías</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1">15+</div>
              <div className="text-sm text-gray-600">Proyectos</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-600 mb-1">3</div>
              <div className="text-sm text-gray-600">Años de investigación</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-1">100%</div>
              <div className="text-sm text-gray-600">Validación académica</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;